// 匯出與證據 › 分享連結（SPEC #vms-web-540 EXP-10，app/vms#17；競品評估 2026-10-17 UX P0-3）：
// 一張表看「現在有哪些影片對外開著、給了誰、誰建的、何時到期、看了幾次」，可單筆或勾選批次撤銷。
// 資料：每個看得到的家戶 GET /v1/homes/{home}/vms/share-links?status=active|all（伺服器只列對工作每一路有 recording.export 的）。
// 識別碼 = 播放頁浮水印上的那 6 碼：外流的畫面拿來這裡對，就知道是給誰的那條連結。
import { useMemo, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { KinioError } from '@kinio/web-sdk';
import { useScope } from '../../lib/scope';
import { useDevices } from '../../lib/useDevices';
import { fullName } from '../../lib/devices';
import { formatDateTime } from '../../lib/tz';
import { rangeStamp } from '../../lib/timefmt';
import { useApp } from '../../lib/AppContext';
import { exportApi, type HomeShareLink } from '../../lib/planeApi';
import { useConfirm } from '../../components/ConfirmDialog';
import { ExportsTabs } from './ExportsPage';
import { statusClass, viewsText } from './share';

interface Row {
  l: HomeShareLink;
  api: string;
  homeId: string;
}

export function SharesPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const all = sp.get('all') === '1';
  const { visible } = useScope();
  const { tree, cameras } = useDevices();
  const me = useApp().session?.user?.user_id ?? '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { ask, dialog } = useConfirm();
  const qs = useQueries({
    queries: visible.map((h) => ({
      queryKey: ['share-links', h.home_id, all],
      queryFn: () => exportApi.homeShareLinks(h.endpoints.api, h.home_id, all),
    })),
  });
  const rows: Row[] = useMemo(
    () =>
      qs
        .flatMap((q, i) => (q.data ?? []).map((l) => ({ l, api: visible[i].endpoints.api, homeId: visible[i].home_id })))
        .sort((a, b) => b.l.created_at.localeCompare(a.l.created_at)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qs.map((q) => q.dataUpdatedAt).join(','), visible],
  );
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const loading = qs.some((q) => q.isLoading);
  const loadErr = qs.find((q) => q.error)?.error;
  const camName = (homeId: string, ref: string) => {
    const c = cameras.find((x) => x.homeId === homeId && x.aid === ref);
    return c ? fullName(c, tree) : ref;
  };
  const when = (s: string) => formatDateTime(Date.parse(s) / 1000, tz, i18n.language);
  const activeRows = rows.filter((r) => r.l.status === 'active');
  const pickedRows = activeRows.filter((r) => picked.has(r.l.link_id));
  const errText = (e: unknown) =>
    e instanceof KinioError ? t(`exports.err_${e.code}`, { defaultValue: e.code }) : String(e);

  async function revoke(list: Row[]) {
    setBusy(true);
    const failed: Record<string, string> = {};
    for (const r of list) {
      try {
        await exportApi.revoke(r.api, r.homeId, r.l.job_id, r.l.link_id);
      } catch (e) {
        failed[r.l.link_id] = errText(e);
      }
    }
    setErrs(failed);
    setPicked(new Set(Object.keys(failed)));
    setBusy(false);
    await qc.invalidateQueries({ queryKey: ['share-links'] });
  }

  async function revokeOne(r: Row) {
    const ok = await ask({
      danger: true,
      title: t('shares.revoke_one_title'),
      body: t('shares.revoke_body', { who: r.l.recipient || t('exports.no_recipient'), code: r.l.code }),
      action: t('exports.revoke'),
    });
    if (ok) await revoke([r]);
  }

  async function revokePicked() {
    const ok = await ask({
      danger: true,
      title: t('shares.revoke_many_title', { n: pickedRows.length }),
      body: t('shares.revoke_many_body'),
      action: t('shares.revoke_picked', { n: pickedRows.length }),
    });
    if (ok) await revoke(pickedRows);
  }

  const toggle = (id: string) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <section data-testid="shares-page">
      <h1 className="page-title">{t('exports.title')}</h1>
      <ExportsTabs />
      <p className="muted small">{t('shares.help')}</p>
      <div className="toolbar">
        <div className="seg" role="group" aria-label={t('shares.filter')}>
          <button aria-pressed={!all} onClick={() => setSp({}, { replace: true })}>
            {t('shares.f_active')}
          </button>
          <button aria-pressed={all} onClick={() => setSp({ all: '1' }, { replace: true })}>
            {t('shares.f_all')}
          </button>
        </div>
        <span className="spacer" />
        <button className="btn btn-sm" disabled={busy || pickedRows.length === 0} onClick={() => void revokePicked()} data-testid="shares-revoke-picked">
          {t('shares.revoke_picked', { n: pickedRows.length })}
        </button>
      </div>
      {loadErr && <p className="error">{errText(loadErr)}</p>}
      {rows.length === 0 && !loading && !loadErr && <p className="muted" data-testid="shares-empty">{all ? t('shares.none_all') : t('shares.none_active')}</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    aria-label={t('shares.pick_all')}
                    checked={activeRows.length > 0 && pickedRows.length === activeRows.length}
                    disabled={activeRows.length === 0}
                    onChange={(e) => setPicked(e.target.checked ? new Set(activeRows.map((r) => r.l.link_id)) : new Set())}
                  />
                </th>
                <th>{t('shares.col_video')}</th>
                <th>{t('shares.col_recipient')}</th>
                <th>{t('shares.col_code')}</th>
                <th>{t('shares.col_by')}</th>
                <th>{t('shares.col_created')}</th>
                <th>{t('shares.col_expires')}</th>
                <th>{t('shares.col_views')}</th>
                <th>{t('shares.col_password')}</th>
                <th>{t('shares.col_status')}</th>
                <th aria-label={t('shares.col_actions')} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const l = r.l;
                const active = l.status === 'active';
                return (
                  <tr key={l.link_id} data-link={l.link_id} data-status={l.status}>
                    <td>
                      {active && (
                        <input
                          type="checkbox"
                          aria-label={t('shares.pick', { code: l.code })}
                          checked={picked.has(l.link_id)}
                          onChange={() => toggle(l.link_id)}
                        />
                      )}
                    </td>
                    <td>
                      {l.sources.map((s) => (
                        <div key={s.device_ref}>
                          <span>{camName(r.homeId, s.device_ref)}</span>{' '}
                          <span className="muted small" data-testid="share-range">
                            {rangeStamp(s.from, s.to, tz)}
                          </span>
                        </div>
                      ))}
                    </td>
                    <td>{l.recipient || <span className="muted">{t('exports.no_recipient')}</span>}</td>
                    <td className="mono" title={l.link_id}>
                      {l.code}
                    </td>
                    <td>{l.created_by === me ? t('shares.by_me') : l.created_by_name || t('shares.by_other')}</td>
                    <td className="small">{when(l.created_at)}</td>
                    <td className="small">{when(l.expires_at)}</td>
                    <td className="small">{viewsText(l.views, l.max_views, t('exports.unlimited'))}</td>
                    <td className="small">{l.has_password ? t('exports.has_password') : '—'}</td>
                    <td>
                      <span className={statusClass(l.status)}>{t(`shares.st_${l.status}`)}</span>
                      {errs[l.link_id] && <div className="error small">{errs[l.link_id]}</div>}
                    </td>
                    <td>
                      {active && (
                        <button className="btn btn-sm" disabled={busy} onClick={() => void revokeOne(r)}>
                          {t('exports.revoke')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {dialog}
    </section>
  );
}
